import { createAsyncThunk } from '@reduxjs/toolkit';
import { http } from '@core';
import type { RootState } from '@core';
import type { SeatData, DeviceType, SeatResources } from '../models/types';
import { normalizeExternalSeats, type RawSeatSchemaEntry } from '../utils/seatResources';
import { normalizeSeatPhotoRules } from '../utils/seatPreview';
import { parseJson } from '../utils/json';

const DEFAULT_HALL_TYPE = 'NAVKA_ARENA';
const SEAT_RESOURCES_LOAD_ERROR = 'Не удалось загрузить ресурсы схемы';

interface SeatResourcesResponse {
  schemaSvg?: string;
  schemaJson?: string;
  seatPhotoRules?: string;
  seatMapRules?: string;
}

export const getSeatResources = createAsyncThunk<
  SeatResources,
  { showId: number; deviceType?: DeviceType; eventId: number },
  { rejectValue: string }
>(
  'seatSelection/getSeatResources',
  async ({ showId, deviceType, eventId }, thunkApi) => {
    try {
      const resourcesRequest = http.get<SeatResourcesResponse>(
        `/api/v1/shows/${showId}/resources`,
        { params: { hallType: DEFAULT_HALL_TYPE, deviceType, eventId } }
      );

      const seatsRequest = http.get<SeatData[]>(
        `/api/v1/catalog/shows/${showId}/events/${eventId}/seats`
      );

      const [resourcesResponse, seatsResponse] = await Promise.all([
        resourcesRequest,
        seatsRequest,
      ]);

      if (!Array.isArray(seatsResponse.data)) {
        return thunkApi.rejectWithValue(SEAT_RESOURCES_LOAD_ERROR);
      }

      const rawSchemaJson = resourcesResponse.data?.schemaJson;
      const parsedSchemaJson = parseJson<RawSeatSchemaEntry[]>(rawSchemaJson) ?? [];
      const seatPhotoRules = normalizeSeatPhotoRules(resourcesResponse.data?.seatPhotoRules);
      const seatMapRules = parseJson<unknown>(resourcesResponse.data?.seatMapRules);

      return {
        schemaSvg: resourcesResponse.data?.schemaSvg ?? null,
        seatmap: normalizeExternalSeats(parsedSchemaJson),
        renderRules: {
          seatPhotoRules,
          seatMapRules,
        },
        seats: seatsResponse.data,
      };
    } catch {
      return thunkApi.rejectWithValue(SEAT_RESOURCES_LOAD_ERROR);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return !state.seatSelection.isSeatResourcesLoading;
    },
  }
);
